// The agent's Google access token for «Notificar mismatch» — the SECOND credential the SPA holds,
// beside Firebase's ID token, and a wider one: the `spreadsheets` scope is not per file, it reaches
// every sheet the agent can edit. So it lives in this module's memory and nowhere else — never
// localStorage, sessionStorage, a cookie or a `window` global — is requested in the agent's own
// gesture, and nothing exports the variable that holds it: callers get it through
// getGoogleAccessToken, and only the Sheets client calls that.
//
// **The trap this does NOT fall into:** adding the scope to the GoogleAuthProvider the Firebase login
// already uses. It works exactly once — Firebase hands the access token over in the sign-in result,
// never refreshes it and never persists it — so an hour later every send fails with no visible cause.
// Google Identity Services' token client is the tool that re-issues it, silently once consent exists.
import { ApiError } from '@citibox-panel/api-client';

const SPREADSHEETS_SCOPE = 'https://www.googleapis.com/auth/spreadsheets';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

interface TokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
}

interface TokenClient {
  requestAccessToken(options?: { prompt?: string }): void;
}

interface OAuth2 {
  initTokenClient(config: {
    client_id: string;
    scope: string;
    callback: (response: TokenResponse) => void;
    error_callback?: (error: { type: string }) => void;
  }): TokenClient;
}

// Optional all the way down: another Google library on the page can define `window.google` without
// the token client, so only `oauth2` itself proves Identity Services has loaded.
declare global {
  interface Window {
    google?: { accounts?: { oauth2?: OAuth2 } };
  }
}

const loadedOAuth2 = (): OAuth2 | undefined => window.google?.accounts?.oauth2;

// Refreshed a minute early, so a token never expires between being handed out and reaching Google.
const EXPIRY_MARGIN_MS = 60_000;

// Google's standard access-token lifetime, for a reply that omits `expires_in`. Without a default,
// the token would read as already expired and every send would re-ask Google.
const DEFAULT_EXPIRES_IN_S = 3600;

// Module state on purpose (see the header). Lost on reload, which costs one silent re-issue.
let cached: { token: string; expiresAt: number } | null = null;

// Bumped on every forget: a request still in flight across a sign-out must not cache its token for
// the next person.
let generation = 0;

// For the Sheets client when Google answers 401 (the token was revoked or expired early, so the next
// send must ask for a fresh one instead of replaying the refused one) and for the session reset.
export function forgetGoogleAccessToken(): void {
  cached = null;
  generation += 1;
}

const cachedIsValid = (): boolean => cached !== null && Date.now() < cached.expiresAt;

// Whether a usable token is already in memory — never the token itself. For the check an «Entrega»
// runs as it opens: outside the agent's click, asking Google for a token would open its popup (or
// get it blocked), so without one in memory the check does not read the sheet at all.
export function hasGoogleAccessToken(): boolean {
  return cachedIsValid();
}

export async function getGoogleAccessToken(clientId: string): Promise<string> {
  if (cached !== null && cachedIsValid()) return cached.token;
  const requestedIn = generation;
  const oauth2 = await loadGis();
  const { token, expiresIn } = await requestToken(oauth2, clientId);
  if (generation !== requestedIn) throw sessionChanged();
  cached = { token, expiresAt: Date.now() + expiresIn * 1000 - EXPIRY_MARGIN_MS };
  return token;
}

// The popup's own failures, translated to the panel's codes. Anything unrecognized is «Google did not
// answer», never «the agent said no»: the agent's copy must not blame them for a misconfiguration.
const POPUP_FAILURES: Record<string, string> = {
  popup_closed: 'SHEETS_CONSENT_DISMISSED',
  popup_failed_to_open: 'SHEETS_POPUP_BLOCKED',
};

const failure = (code: string): ApiError =>
  new ApiError(0, code, null, `Google token request failed: ${code}`);

const sessionChanged = (): ApiError =>
  new ApiError(
    0,
    'SHEETS_SESSION_CHANGED',
    null,
    'Google token discarded: the session changed while it was requested',
  );

// Loaded on first use rather than from index.html: most sessions never send a report. Memoized so
// two concurrent sends inject one script; a failed load clears the memo, so the next send retries.
let gisLoading: Promise<OAuth2> | null = null;

function loadGis(): Promise<OAuth2> {
  const ready = loadedOAuth2();
  if (ready) return Promise.resolve(ready);
  gisLoading ??= new Promise<OAuth2>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.addEventListener('load', () => {
      const loaded = loadedOAuth2();
      if (loaded) resolve(loaded);
      else reject(failure('SHEETS_UNAVAILABLE'));
    });
    script.addEventListener('error', () => {
      reject(failure('SHEETS_UNAVAILABLE'));
    });
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    gisLoading = null;
    document.head.querySelector(`script[src="${GIS_SRC}"]`)?.remove();
    throw error;
  });
  return gisLoading;
}

// `prompt: ''` shows the consent popup only the first time; once granted, the re-issue is silent.
function requestToken(
  oauth2: OAuth2,
  clientId: string,
): Promise<{ token: string; expiresIn: number }> {
  return new Promise((resolve, reject) => {
    const client = oauth2.initTokenClient({
      client_id: clientId,
      scope: SPREADSHEETS_SCOPE,
      callback: (response) => {
        if (response.access_token !== undefined && response.error === undefined) {
          resolve({
            token: response.access_token,
            expiresIn: response.expires_in ?? DEFAULT_EXPIRES_IN_S,
          });
        } else if (response.error === 'access_denied') reject(failure('SHEETS_CONSENT_DISMISSED'));
        else reject(failure('SHEETS_UNAVAILABLE'));
      },
      error_callback: (error) => {
        reject(failure(POPUP_FAILURES[error.type] ?? 'SHEETS_UNAVAILABLE'));
      },
    });
    client.requestAccessToken({ prompt: '' });
  });
}
